import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, apiError, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    loadMe();
  }, [loadMe]);

  const applySession = (data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    const merged = {
      ...data.user,
      needs_profile: Boolean(data.needs_profile),
      missing_profile_fields: data.missing_profile_fields || [],
      profile_completion: data.profile_completion,
      effective_roles: data.effective_roles || [],
    };
    setUser(merged);
    return merged;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    return applySession(data);
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return applySession(data);
  };

  const googleLogin = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sessionId } });
    applySession(data);
    return { user: data.user, needsRoleSelection: Boolean(data.needs_profile),
      needsProfile: Boolean(data.needs_profile) };
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const refreshUser = loadMe;

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, googleLogin, logout, refreshUser, setUser, apiError }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
